import type { ToolkitPropertyRowKey } from '@aloft/toolkit-shared';
import { ToolkitPropertyRowMap, getDisplayValueFunc } from '@aloft/toolkit-shared';

import type { AdjustGridRowSchema, AdjustGridSchema } from './types.js';

const getAdjustRow = (key: ToolkitPropertyRowKey): AdjustGridRowSchema => {
  const { label, adjustProps, isHiddenForSubject } = ToolkitPropertyRowMap[key];
  const { manualTerm, managedTerms, isRequired } = adjustProps ?? {
    manualTerm: undefined,
    managedTerms: undefined,
    isTimeAdjustRow: undefined,
  };
  const terms = manualTerm ? [manualTerm, ...(managedTerms ?? [])] : [];
  return {
    key,
    label: label as AdjustGridRowSchema['label'],
    getDisplayValue: getDisplayValueFunc(key),
    terms,
    isHiddenForSubject,
    isRequired,
  };
};

const getAdjustRows = (...keys: ToolkitPropertyRowKey[]): AdjustGridRowSchema[] =>
  keys.map(getAdjustRow);

/**
 * A generic adjustment grid, heavily based on the FNMA 1004. If we ever have
 * more than one of these, please move them into a folder called "grids"
 */
export const gridGeneric: AdjustGridSchema = {
  rowGroups: [
    {
      key: 'header',
      rows: getAdjustRows(
        'salePrice',
        'salesPricePerGrossLivingArea',
        'dataSources',
        'verificationSources',
      ),
    },
    {
      key: 'sale',
      costKey: 'market',
      rows: getAdjustRows('saleType', 'financialConcessions', 'dateOfSale'),
    },
    {
      key: 'site',
      costKey: 'site',
      rows: getAdjustRows('leaseholdOrFeeSimple', 'site', 'location', 'view'),
    },
    {
      key: 'design',
      costKey: 'design',
      rows: getAdjustRows('design', 'quality'),
    },
    {
      key: 'condition',
      costKey: 'condition',
      rows: getAdjustRows('age', 'condition'),
    },
    {
      key: 'dwelling',
      costKey: 'dwelling',
      rows: getAdjustRows(
        'grossLivingArea',
        'aboveGradeRoomCount',
        'heatingCooling',
        'energyEfficientItems',
      ),
    },
    {
      key: 'basement',
      costKey: 'basement',
      rows: getAdjustRows('totalBasementArea', 'belowGradeRoomCount'),
    },
    {
      key: 'functionalUtility',
      costKey: 'functionalUtility',
      rows: getAdjustRows('functionalUtility'),
    },
    {
      key: 'garage',
      costKey: 'garage',
      rows: getAdjustRows('garage'),
    },
    {
      key: 'porchPatioDeck',
      costKey: 'porchPatioDeck',
      rows: getAdjustRows('porchPatioDeck'),
    },
    {
      key: 'additionalFeatures',
      costKey: 'additionalFeatures',
      rows: getAdjustRows(
        'additionalFeature1Description',
        'additionalFeature2Description',
        'additionalFeature3Description',
      ),
    },
  ],
  modeledOut: [
    // Hack - we don't want listToSale to be affected by modeling
    // and it doesn't affect anything else so putting it first
    'listToSale',
    'dateOfSale',
    'grossLivingArea',
    'site',
  ],
};
